import type { QueryClient } from '@tanstack/react-query';
import type { ToastOptions } from '@waymail/ui';
import { descreverAdiamento, horarioDeAdiamento, type QuandoAdiar } from './adiar';
import { api, type Conta, type CorpoDaApi, type MensagemDaApi } from './api';
import { chaves, type VarsAdiar } from './consultas';
import type { Rascunho } from './rascunho';
import { rascunhoDeResposta, type TipoDeResposta } from './resposta';

/*
 * O que o menu de contexto faz alem da triagem: responder e adiar. Num modulo
 * a parte porque so roda depois de um clique no menu, e o bundle inicial tem
 * teto (Secao 14.1).
 */

type Aviso = (opcoes: ToastOptions) => void;

/** Responder precisa da mensagem inteira; a linha da lista so tem o resumo. */
export async function responder(
  entrada: {
    cliente: QueryClient;
    id: string;
    tipo: TipoDeResposta;
    contas: Conta[];
    responderAcima: boolean;
    abrir: (rascunho: Rascunho) => void;
  },
  toast: Aviso,
): Promise<void> {
  const { cliente, id, contas } = entrada;
  try {
    const staleTime = 30_000;
    const [mensagem, corpo] = await Promise.all([
      cliente.fetchQuery({
        queryKey: chaves.mensagem(id),
        queryFn: () => api<MensagemDaApi>(`/messages/${id}`),
        staleTime,
      }),
      // Sem imagens remotas, como a leitura de um remetente desconhecido.
      cliente.fetchQuery({
        queryKey: chaves.corpo(id, false),
        queryFn: () => api<CorpoDaApi>(`/messages/${id}/body?images=blocked`),
        staleTime,
      }),
    ]);
    // A conta que recebeu a mensagem assina a resposta.
    const minha = contas.find((c) => c.id === mensagem.accountId) ?? contas[0];
    entrada.abrir(
      rascunhoDeResposta({
        tipo: entrada.tipo,
        mensagem,
        corpoHtml: corpo.html,
        meusEnderecos: contas.map((c) => c.email),
        identidade: minha?.email ?? '',
        responderAcima: entrada.responderAcima,
      }),
    );
  } catch (e) {
    toast({
      title: e instanceof Error ? e.message : 'Não foi possível abrir a resposta.',
      tone: 'error',
    });
  }
}

export async function adiarPara(
  entrada: {
    ids: string[];
    quando: QuandoAdiar;
    adiar: (vars: VarsAdiar) => Promise<unknown>;
    aoConcluir: () => void;
  },
  toast: Aviso,
): Promise<void> {
  const alvo = horarioDeAdiamento(entrada.quando);
  try {
    await Promise.all(
      entrada.ids.map((messageId) => entrada.adiar({ messageId, until: alvo.toISOString() })),
    );
    entrada.aoConcluir();
    toast({
      title: `Volta em ${descreverAdiamento(alvo)}.`,
      action: {
        label: 'Desfazer',
        onAction: () => {
          for (const messageId of entrada.ids) void entrada.adiar({ messageId, until: null });
        },
      },
    });
  } catch (e) {
    toast({ title: e instanceof Error ? e.message : 'Não foi possível adiar.', tone: 'error' });
  }
}
